import { useEffect, useRef } from 'react';

// Modal focus, as in DefaultSyllabi: move focus into the dialog, keep Tab
// inside it, close on Escape (unless busy), make the page behind inert, and
// put focus back on close.
export default function useDialogFocus(ref, onClose, busy) {
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  onCloseRef.current = onClose;
  busyRef.current = busy;
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const previous = document.activeElement;
    // Everything behind the dialog: the page content and the app header (the
    // header is a sibling of the page, outside .pr-page).
    const page = root.closest('.pr-page');
    const behind = [
      page && page.querySelector(':scope > .wrap'),
      ...(page && page.parentElement ? [...page.parentElement.children].filter((el) => el !== page) : []),
    ].filter(Boolean);
    behind.forEach((el) => {
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
    });
    const focusables = () =>
      [...root.querySelectorAll('button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])')].filter(
        (el) => !el.disabled && el.offsetParent !== null
      );
    (root.querySelector('[data-autofocus]') || focusables()[0])?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape' && !busyRef.current) {
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current();
      } else if (e.key === 'Tab') {
        const list = focusables();
        if (!list.length) {
          e.preventDefault();
          root.focus();
          return;
        }
        const first = list[0];
        const last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    root.addEventListener('keydown', onKey);
    return () => {
      root.removeEventListener('keydown', onKey);
      behind.forEach((el) => {
        el.removeAttribute('inert');
        el.removeAttribute('aria-hidden');
      });
      if (previous && document.contains(previous)) previous.focus();
    };
  }, [ref]);
}
